import React, { FunctionComponent } from 'react';
import styled from 'styled-components';

import { Heading, Text } from 'components/chroma';
import THEME from 'theme';
import {
  ApiErrorReasonType,
  ErrorPageApiErrorReasonType,
  InsuranceName,
  InsuredItem,
} from 'scenes/common/types';
import {
  customerCareNumber,
  findAnInsurerUrl,
  format1300Number,
  insuranceBaseUrl,
  toDisplayUrl,
} from 'scenes/common/helpers/contacts';
import { toPercentage } from 'scenes/common/helpers/percentage';
import { DEFAULT_ONLINE_CAMPAIGN } from 'scenes/common/helpers/insuranceInfo';

import { Referral } from './components/Referral';
import ErrorPage from './ErrorPage';

export const InlineButton = styled.button`
  background: none !important;
  border: none;
  padding: 0 !important;
  font-size: 1rem !important;
  font-family: 'Source Sans Pro', sans-serif;
  text-decoration: underline;
  color: ${THEME.LINK.DEFAULT};
  cursor: pointer;

  &:hover {
    text-decoration: underline;
    background-color: ${THEME.LINK.HOVER} !important;
  }
`;

export const QuoteErrorPage: FunctionComponent<{
  quoteNumber?: string;
  insuranceName: InsuranceName;
  itemInsured: InsuredItem;
  errorReasonType: ErrorPageApiErrorReasonType;
  specificImages: {
    [ApiErrorReasonType.Declined]: FunctionComponent;
    [ApiErrorReasonType.Embargo]: FunctionComponent;
    [ApiErrorReasonType.Unknown]: FunctionComponent;
  };
  onRestartQuote: () => void;
  onClickCallUs: () => void;
}> = ({
  quoteNumber,
  insuranceName,
  itemInsured,
  specificImages,
  errorReasonType,
  onRestartQuote,
  onClickCallUs,
}) => {
  const ErrorContent: {
    [a in ErrorPageApiErrorReasonType]: {
      Header: FunctionComponent;
      Image: FunctionComponent;
      Content: FunctionComponent;
      ShowNewQuote: boolean;
    };
  } = {
    [ApiErrorReasonType.Declined]: {
      Header: () => (
        <Heading>We are unable to offer you {insuranceName} Insurance</Heading>
      ),
      Image: specificImages[ApiErrorReasonType.Declined],
      Content: () => (
        <>
          <Text>
            Unfortunately, we are unable to offer you Coles {insuranceName}{' '}
            Insurance as your particular situation falls outside our rules for
            providing insurance.
          </Text>
          <Heading size={2} margin={{ top: 7, bottom: 2 }}>
            What next?
          </Heading>
          <Text>
            If you have completed everything correctly and would like further
            information about what we relied on for our decision please call us
            on{' '}
            <a onClick={onClickCallUs} href={`tel:${customerCareNumber()}`}>
              {format1300Number(customerCareNumber())}
            </a>
            . If you think you have made a mistake in entering your details, you
            can start a <InlineButton type="submit">new quote</InlineButton>.
          </Text>
          <Text>
            For information on your options of alternative insurance you can
            access the Insurance Council of Australia&#39;s &#39;Find an
            Insurer&#39; service at{' '}
            <a
              href={findAnInsurerUrl()}
              target="_blank"
              rel="noopener noreferrer"
            >
              {toDisplayUrl(findAnInsurerUrl())}
            </a>
            .
          </Text>
          <Text>
            If you are unhappy with our decision, you can access our complaints
            process by calling us on{' '}
            <a onClick={onClickCallUs} href={`tel:${customerCareNumber()}`}>
              {format1300Number(customerCareNumber())}
            </a>
            .
          </Text>
        </>
      ),
      ShowNewQuote: false,
    },
    [ApiErrorReasonType.Referral]: {
      Header: () => <Heading>We need to talk to you</Heading>,
      Image: () => <Referral />,
      Content: () => (
        <>
          <Text>
            We need to clarify a few things with you over the phone. Please call
            us on{' '}
            <a onClick={onClickCallUs} href={`tel:${customerCareNumber()}`}>
              {format1300Number(customerCareNumber())}
            </a>{' '}
            and mention your Quote No: {quoteNumber}.
          </Text>
          <Text>
            If you choose to purchase a {insuranceName} Insurance policy, you
            still get the {toPercentage(DEFAULT_ONLINE_CAMPAIGN.discount)}{' '}
            online discount.
          </Text>
        </>
      ),
      ShowNewQuote: false,
    },
    [ApiErrorReasonType.Embargo]: {
      Header: () => (
        <Heading>We are unable to offer you {insuranceName} Insurance</Heading>
      ),
      Image: specificImages[ApiErrorReasonType.Embargo],
      Content: () => (
        <>
          <Text>
            Unfortunately, your {itemInsured} can not be insured due to current
            weather or potential weather conditions in the area.
          </Text>
          <Heading size={2} margin={{ top: 7, bottom: 2 }}>
            What next?
          </Heading>
          <Text>
            If you have completed everything correctly and would like further
            information about what we relied on for our decision please call us
            on{' '}
            <a onClick={onClickCallUs} href={`tel:${customerCareNumber()}`}>
              {format1300Number(customerCareNumber())}
            </a>
            . If you think you have made a mistake in entering your details, you
            can start a <InlineButton type="submit">new quote</InlineButton>.
          </Text>
          <Text>
            For information on your options of alternative insurance you can
            access the Insurance Council of Australia&#39;s &#39;Find an
            Insurer&#39; service at{' '}
            <a
              href={findAnInsurerUrl()}
              target="_blank"
              rel="noopener noreferrer"
            >
              {toDisplayUrl(findAnInsurerUrl())}
            </a>
            .
          </Text>
          <Text>
            If you are unhappy with our decision, you can access our complaints
            process by calling us on{' '}
            <a onClick={onClickCallUs} href={`tel:${customerCareNumber()}`}>
              {format1300Number(customerCareNumber())}
            </a>
            .
          </Text>
        </>
      ),
      ShowNewQuote: false,
    },
    [ApiErrorReasonType.ApiError]: {
      Header: () => <Heading>Something went wrong</Heading>,
      Image: () => <div />,
      Content: () => <div />,
      ShowNewQuote: true,
    },
    [ApiErrorReasonType.ApiLimitReached]: {
      Header: () => <Heading>Limit Reached</Heading>,
      Image: () => <div />,
      Content: () => <div />,
      ShowNewQuote: true,
    },
    [ApiErrorReasonType.Unknown]: {
      Header: () => (
        <Heading>We’re feeling a little under the weather!</Heading>
      ),
      Image: specificImages[ApiErrorReasonType.Unknown],
      Content: () => (
        <>
          <Text>
            We&#39;re sorry but this service is temporarily unavailable. We
            apologise for any inconvenience. Please try again later.
          </Text>
          <Text weight={700}>
            Alternatively, please call us on{' '}
            <a onClick={onClickCallUs} href={`tel:${customerCareNumber()}`}>
              {format1300Number(customerCareNumber())}
            </a>
          </Text>
          <Text>
            Return to the{' '}
            <a
              href={insuranceBaseUrl()}
              target="_blank"
              rel="noopener noreferrer"
            >
              Coles Insurance home page
            </a>
          </Text>
        </>
      ),
      ShowNewQuote: false,
    },
  };

  const { Header, Image, Content, ShowNewQuote } = ErrorContent[
    errorReasonType
  ];

  return (
    <ErrorPage
      Content={Content}
      Header={Header}
      Image={Image}
      showNewQuote={ShowNewQuote}
      onRestartQuote={onRestartQuote}
    />
  );
};

export default QuoteErrorPage;
